// @workflow assistant.attachments, assistant.send-message
/**
 * The one seam between the AVA Assistant UI and the agent that answers it: always the aiva.api
 * agent loop over SSE (sseAgentClient.js), emitting the events of
 * .claude/features/AVA_ASSISTANT_EVENT_CONTRACT.md. There is no scripted or offline agent in the
 * app; the history list and every replay come from the server too.
 *
 * start(prompt, onEvent, options) returns { stop(), resolve(requestId, response) }; resolve answers
 * a client.request and is idempotent. stop() emits message.end { stopped: true } itself.
 */
import config from '@/../appConfig';
import http from '@/utils/httpService';
import { $getAuthHeaders } from '@/utils';
import { createSseAgentClient } from '@Assistant/services/sseAgentClient';
import { UI_TOOL_NAMES } from '@Assistant/domain/uiTools';
import { CLIENT_DRAWS } from '@Assistant/domain/clientDraws';

/**
 * Sent on every message: the server offers the model only the UI tools this build can run, sends
 * only the reply blocks it draws (contract "Reply blocks"; any other kind it writes as text), and
 * offers none of the tool groups the user turned off in Settings.
 */
const clientInfo = (toolGroupsOff = []) => ({ uiTools: UI_TOOL_NAMES, blocks: CLIENT_DRAWS, toolGroupsOff });

/** The cheapest authenticated call: it lets httpService's 401 handling refresh the token. */
const TOKEN_PROBE = { url: 'api/assistant/conversations', params: { page: 1, itemsPerPage: 1 } };

/**
 * fetch() bypasses axios, so a 401 can't use httpService's refresh directly. Instead of a second
 * copy of that logic, one small authenticated call goes through httpService: its interceptor
 * refreshes the token (or signs the user out). True when a new token is now stored.
 * Also used by the dictation and Read aloud clients (services/apiAuth.js).
 */
export async function refreshAuth() {
  const before = $getAuthHeaders().Authorization;
  try {
    await http.get(TOKEN_PROBE);
  } catch (err) {
    return false;
  }
  const after = $getAuthHeaders().Authorization;
  return !!after && after !== before;
}

const sse = createSseAgentClient({ baseUrl: config.API_URL, getHeaders: () => $getAuthHeaders(), refreshAuth });

export default {
  /**
   * @param {string} prompt
   * @param {(event: object) => void} onEvent
   * @param {{ conversationId: string, context?: object, segments?: Array, attachments?: string[],
   *   retry?: { versionOf: string, version: number, reason?: string, comment?: string }, toolGroupsOff?: string[] }} options
   *   attachments = ids of uploaded files. retry = Try again.
   */
  start(prompt, onEvent, { conversationId, context = null, segments, attachments = [], retry = null, toolGroupsOff = [] } = {}) {
    return sse.start(prompt, onEvent, { conversationId, context, segments, attachments, client: clientInfo(toolGroupsOff), retry });
  },

  /** The signed-in user's conversations from the server (GET api/assistant/conversations). */
  listConversations(options) {
    return sse.listConversations(options);
  },

  /** A stored conversation's events for replay (GET …/conversations/{id}/events). */
  conversationEvents(conversationId) {
    return sse.conversationEvents(conversationId);
  },

  /** What the assistant works from in this conversation now (POST …/conversations/{id}/context). */
  conversationContext(conversationId, context) {
    return sse.conversationContext(conversationId, context);
  },

  /** Deletes a conversation for the user (server: archive). */
  archiveConversation(conversationId) {
    return sse.archiveConversation(conversationId);
  },

  /**
   * Saves the user's rating of one reply version (POST …/messages/{messageId}/feedback).
   * @param {{ conversationId: string, messageId: string, feedback: { rating: string|null, reason?: string|null, comment?: string } }} payload
   * @returns {Promise<void>}
   */
  sendFeedback(payload) {
    return sse.sendFeedback(payload);
  },
};
